import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useOutfitStore } from '@/stores/outfitStore';
import type { OutfitCategory } from '@/types';

interface ImageUploadProps {
  category: OutfitCategory;
  existingImages: string[];
  maxImages?: number;
  outfitId: number;
  onImagesChange: (images: string[]) => void;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  category,
  existingImages,
  maxImages = 4,
  outfitId,
  onImagesChange,
}) => {
  const { t } = useTranslation();
  const { uploadImage, deleteImage, updateOutfit } = useOutfitStore();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (existingImages.length >= maxImages) return;
    setUploadError(null);

    const remainingSlots = maxImages - existingImages.length;
    const filesToUpload = acceptedFiles.slice(0, remainingSlots);

    // Client-side validation
    for (const file of filesToUpload) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError(t('upload.invalidType', 'Only JPG, PNG, WebP and HEIC files are allowed'));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(t('upload.tooLarge', 'File size must be under 10 MB'));
        return;
      }
    }

    setUploading(true);

    const newImages: string[] = [];
    let failed = 0;

    for (const file of filesToUpload) {
      const url = await uploadImage(file, category);
      if (url) {
        newImages.push(url);
      } else {
        failed++;
      }
    }

    if (failed > 0) {
      setUploadError(t('upload.failed', { count: failed, defaultValue: `${failed} file(s) failed to upload` }));
    }

    if (newImages.length > 0) {
      const updatedImages = [...existingImages, ...newImages];
      onImagesChange(updatedImages);
      await updateOutfit(outfitId, { [`${category}_images`]: updatedImages });
    }

    setUploading(false);
  }, [existingImages, maxImages, category, outfitId, uploadImage, updateOutfit, onImagesChange, t]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.heic'] },
    disabled: existingImages.length >= maxImages || uploading,
  });

  const handleRemoveImage = async (index: number) => {
    const imageUrl = existingImages[index];
    const filename = imageUrl.split('/').pop();

    if (filename) {
      await deleteImage(filename);
    }

    const updatedImages = existingImages.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
    await updateOutfit(outfitId, { [`${category}_images`]: updatedImages });
  };

  const canAddMore = existingImages.length < maxImages;

  return (
    <div className="w-full">
      {existingImages.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {existingImages.map((url, index) => (
            <div key={index} className="relative group aspect-[9/16] bg-secondary rounded-2xl">
              <img
                src={url}
                alt={`${category} ${index + 1}`}
                className="w-full h-full object-cover rounded-2xl border border-border shadow-sm transition-transform hover:scale-[1.02]"
              />
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {uploadError && (
        <div className="mb-3 px-3 py-2 bg-destructive/10 border border-destructive/30 rounded-xl text-xs font-medium text-destructive flex items-center justify-between">
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {canAddMore && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all duration-300
            ${isDragActive ? 'border-primary bg-secondary/50 shadow-inner' : 'border-border hover:border-accent hover:bg-secondary/30'}
            ${uploading ? 'opacity-50 pointer-events-none' : ''}
          `}
        >
          <input {...getInputProps()} />

          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="text-sm font-medium text-muted-foreground font-serif italic">
                {t('common.uploading')}
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className={`w-8 h-8 mb-1 transition-colors ${isDragActive ? 'text-primary' : 'text-muted-foreground/40'}`} />
              <div className="text-sm font-medium text-muted-foreground">
                <span>{t('outfit.dragDrop')}</span>
                <span className="mx-1.5 font-normal text-muted-foreground/60">{t('outfit.or')}</span>
                <span className="text-foreground font-bold underline underline-offset-4 decoration-border hover:decoration-primary transition-colors uppercase tracking-wider text-xs">
                  {t('outfit.clickToUpload')}
                </span>
              </div>
              <div className="text-xs font-medium text-muted-foreground/60 mt-2">
                {t('outfit.maxPhotos', { max: maxImages })} &bull; <span className="text-foreground">{existingImages.length}/{maxImages}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
